// 리드 표시 공용 순수 헬퍼 — 전화 추출/포맷, 답변 포맷, KST 시각, 기간 프리셋.
// admin/client 가 복붙하던 것을 단일화(동작 동일). React 비의존.

export interface UserColumn {
  column_id?: string;
  column_name?: string;
  string_value?: string | number | null;
}

export const PHONE_COLUMN_IDS = ["phone", "mobile", "mobile_phone", "phone_number"];

export interface LeadPhoneSource {
  userColumnData?: unknown;
  submittedContent?: unknown;
}

// user_column_data 의 전화 컬럼 우선, 없으면 submittedContent 질문(전화/연락처/핸드폰/휴대)에서 추출.
export function pickPhone(lead: LeadPhoneSource): string | null {
  const ucd = Array.isArray(lead.userColumnData)
    ? (lead.userColumnData as UserColumn[])
    : [];
  for (const c of ucd) {
    if (c?.column_id && PHONE_COLUMN_IDS.includes(c.column_id.toLowerCase())) {
      return c.string_value == null ? null : String(c.string_value);
    }
  }
  const items = Array.isArray(lead.submittedContent)
    ? (lead.submittedContent as Array<{ question?: string; answer?: unknown }>)
    : [];
  for (const q of items) {
    const label = (q.question ?? "").toLowerCase();
    if (
      label.includes("전화") ||
      label.includes("연락처") ||
      label.includes("핸드폰") ||
      label.includes("휴대")
    ) {
      const v = q.answer;
      if (v == null) continue;
      const s = Array.isArray(v) ? v.join("") : String(v);
      if (s.replace(/\D/g, "").length >= 9) return s;
    }
  }
  return null;
}

export function formatPhone(raw: string): string {
  const digits = raw.replace(/\D/g, "");
  if (digits.length === 11) {
    return `${digits.slice(0, 3)}-${digits.slice(3, 7)}-${digits.slice(7)}`;
  }
  if (digits.length === 10) {
    return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  return raw;
}

export function formatAnswer(v: unknown): string {
  if (v == null) return "—";
  if (Array.isArray(v))
    return v.map((x) => (x == null ? "" : String(x))).join(", ");
  return String(v);
}

// UTC → KST(+9h) "YYYY-MM-DD HH:mm". KST 는 DST 없어 고정 오프셋으로 충분.
export function formatKstDateTime(iso: string): string {
  const d = new Date(iso);
  const kst = new Date(d.getTime() + 9 * 60 * 60 * 1000);
  const y = kst.getUTCFullYear();
  const m = String(kst.getUTCMonth() + 1).padStart(2, "0");
  const day = String(kst.getUTCDate()).padStart(2, "0");
  const hh = String(kst.getUTCHours()).padStart(2, "0");
  const mm = String(kst.getUTCMinutes()).padStart(2, "0");
  return `${y}-${m}-${day} ${hh}:${mm}`;
}

// 초 → 사람이 읽는 소요시간. null/음수→"—". <60 "N초" · <3600 "N분" · 그 외 "N시간 M분".
export function formatDuration(seconds: number | null | undefined): string {
  if (seconds == null || seconds < 0) return "—";
  const s = Math.round(seconds);
  if (s < 60) return `${s}초`;
  const mins = Math.round(s / 60);
  if (mins < 60) return `${mins}분`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m === 0 ? `${h}시간` : `${h}시간 ${m}분`;
}

// 영업시간 창 표시. 둘 다 있어야 "10:00–22:00", 아니면 "—".
export function formatBusinessHours(
  open: string | null | undefined,
  close: string | null | undefined,
): string {
  return open && close ? `${open}–${close}` : "—";
}

// 날짜 프리셋 → ISO 범위. all=전체, custom=직접, recent-1d/7d/그외=최근 N일.
export function computeLeadRange(
  preset: string,
  customFrom: string,
  customTo: string,
): { from?: string; to?: string } {
  if (preset === "all") return {};
  if (preset === "custom") {
    return {
      from: customFrom
        ? new Date(`${customFrom}T00:00:00+09:00`).toISOString()
        : undefined,
      to: customTo
        ? new Date(`${customTo}T23:59:59.999+09:00`).toISOString()
        : undefined,
    };
  }
  const days = preset === "recent-1d" ? 1 : preset === "recent-7d" ? 7 : 30;
  const from = new Date();
  from.setDate(from.getDate() - days);
  return { from: from.toISOString() };
}
